import React from "react";
import headingStyles from "./ModalHeadingStyles";

const MentalCalcModalContent = () => (
  <div style={{ textAlign: 'left', fontFamily: 'Raleway, sans-serif' }}>
    <h3 style={headingStyles.h3}>The Art of Mental Calculation</h3>
    <hr />
    <div style={{ fontWeight: 'bold', color: '#000' }}>Math Illustration & Curriculum Design</div>
    <div>College Years (2-year collaboration)</div>
    <p><em>Illustrated and co-authored a mental math curriculum with Dr. Arthur Benjamin, later contributing to two New York Times bestsellers</em></p>
    <hr />
    <div><strong style={{ fontWeight: 800 }}>Project Role:</strong> Illustrator, Curriculum Designer, Co-Author</div>
    <div><strong style={{ fontWeight: 800 }}>Platforms:</strong> Adobe Illustrator, Adobe InDesign, LaTeX</div>
    <div><strong style={{ fontWeight: 800 }}>Deliverables:</strong> Fully illustrated 40-lesson middle school curriculum + illustrations and typesetting for two bestselling books</div>
    <div><strong style={{ fontWeight: 800 }}>Timeline:</strong> 2 years (initial project), followed by continuing work on additional books</div>
    <hr />

    <h4 style={headingStyles.h4}>Project Overview</h4>
      <p>
      I illustrated and co-authored a 40-lesson mental math curriculum with Dr. Arthur Benjamin—work that later evolved into two <strong>New York Times bestselling books</strong>, <em><a href="https://a.co/d/1NnntYn" target="_blank" rel="noreferrer">The Magic of Math</a></em> and <em><a href="https://a.co/d/d0BQU0w" target="_blank" rel="noreferrer">Secrets of Mental Math</a></em>. This project combined my dual background in mathematics and illustration to make abstract arithmetic concepts more visual, memorable, and fun.
      </p>
      <p>
      What began as a college collaboration turned into a long-term creative partnership. I designed hundreds of custom diagrams, typeset full chapters in LaTeX, and helped shape a consistent visual voice across curriculum and trade books. The end result: a reusable visual system that continues to support learners of all ages.
      </p>

    <h4 style={headingStyles.h4}>Problem Statement</h4>
    <ul>
      <li>How can we help middle school students master Dr. Benjamin's mental math techniques through clear visuals and an intuitive layout?</li>
      <li>What design strategies can make abstract arithmetic patterns feel playful, accessible, and fun?</li>
    </ul>

    <h4 style={headingStyles.h4}>The Solution</h4>
    <p>
      I developed hundreds of custom mathematical illustrations that demonstrated patterns in mental addition, subtraction, and number sense. These visuals served as step-by-step scaffolds for learners and complemented Dr. Benjamin’s signature teaching style.
    </p>
    <p>
      We built the book collaboratively across multiple time zones, using Adobe Illustrator for figures and LaTeX for precise mathematical typesetting. The end result was a fully illustrated 120-page curriculum that continues to be used and shared through Dr. Benjamin’s best-selling books.
    </p>

    <h4 style={headingStyles.h4}>Aligning Goals</h4>
    <p>
    This was a passion project and a turning point for me. I learned how to collaborate on large-scale educational books, juggle precision and creativity in production workflows, and bring clarity to complex math through visuals. I also got very good at spotting typos—some of which I may or may not have introduced myself.
    </p>

    <h4 style={headingStyles.h4}>Discover</h4>
    <ul>
      <li>Designed illustrations to support number sense, mental arithmetic tricks, and math storytelling</li>
      <li>Collaborated remotely with Dr. Benjamin to iterate on pedagogical design and layout</li>
      <li>Used LaTeX to typeset answer keys, exercises, and visual layouts with mathematical precision</li>
    </ul>

    <h4 style={headingStyles.h4}>Define</h4>
    <ul>
      <li>Identified which concepts most needed visual scaffolds for student understanding</li>
      <li>Developed a consistent visual style across multiple books and teaching materials</li>
      <li>Balanced clarity, creativity, and educational integrity across hundreds of math diagrams</li>
    </ul>

    <h4 style={headingStyles.h4}>Develop</h4>
    <ul>
      <li>Illustrated and typeset the full original curriculum, plus two 120-page follow-up books</li>
      <li>Produced hundreds of diagrams covering everything from simple shortcuts to algebraic patterns</li>
      <li>Iterated closely with the author to match tone, clarity, and teaching intent</li>
    </ul>

    <h4 style={headingStyles.h4}>Deliver</h4>
    <ul>
      <li><em>The Art of Mental Calculation</em> was published and promoted through Arthur Benjamin’s platforms</li>
      <li>Went on to illustrate <em>The Magic of Math</em> and <em>Secrets of Mental Math</em>, both New York Times bestsellers</li>
      <li>Created a reusable visual design system for educational math content</li>
      <li>Discovered one of my superpowers: making mathematical ideas come alive through design</li>
    </ul>
  </div>
);

export default MentalCalcModalContent;